import React, { useEffect, useState } from "react";
import awnLogo from "../../../../assets/images/awnlogonew.svg";
import bgDesign from "../../../../assets/images/bgDesign.svg";
import "../../forgetPassword/ForgetPassword.scss";
import "../../forgetPassword/newForgetPassword.css";
import "../../login/Login.module.css";
import MainServices from "../../../../services/MainServices";
import umsIcon from "../../../../assets/images/icons/umsIcon.svg";
import crmIcon from "../../../../assets/images/icons/crmIcon.svg";
import edmsIcon from "../../../../assets/images/icons/edmsIcon.svg";
import subscriptionIcon from "../../../../assets/images/icons/subscriptionIcon.svg";
import workflowIcon from "../../../../assets/images/icons/workflowIcon.svg";
import requestIcon from "../../../../assets/images/icons/requestIcon.svg";
import serviceIcon from "../../../../assets/images/icons/serviceIcon.svg";
import PermissionContextFunctions from "../../../../assets/functions/PermissionContextFunctions";
import { notifyWarn } from "../../../../assets/elements/Toast";
import useConfig from "../../../../hooks/useConfig";
import * as Storage from "../../../../store/LocalStorage";
import { useNavigate } from "react-router-dom";
import { allRoutes } from "../../../../assets/routes/allRoutes";

const CheckForPermittedModules = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const [canAccessEmployeeKey, setCanAccessEmployeeKey] = useState(false);
  const [canAccessEmployeeCheck, setCanAccessEmployeeCheck] = useState(false);

  useEffect(() => {
    const isAccess = Storage.get(config?.canAccessEmployeeKey);
    setCanAccessEmployeeKey(isAccess);
    setCanAccessEmployeeCheck(isAccess || grantModulePermissions("UMS"));
  }, [Storage.get(config?.canAccessEmployeeKey)]);

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { getUmsAuthProvider } = MainServices();
  const { grantModulePermissions } = PermissionContextFunctions();

  useEffect(() => {
    getAuthProviders();
  }, []);

  useEffect(() => {
    if (loading || error || !modules || modules.length === 0) return;

    const hasAnyModulePermission = modules.some(
      (m) => m.name !== "UMS" && grantModulePermissions(m.name),
    );

    const hasAnyAccess = modules.some((module) => {
      if (module.name === "UMS") {
        if (hasAnyModulePermission) {
          return grantModulePermissions("UMS");
        } else {
          return canAccessEmployeeCheck !== "false";
        }
      }
      return grantModulePermissions(module.name);
    });

    if (!hasAnyAccess) {
      navigate(config?.myProfile);
    }
  }, [modules, loading, error, canAccessEmployeeCheck]);

  const iconMap = {
    UMS: umsIcon,
    CRM: crmIcon,
    EDMS: edmsIcon,
    WORKFLOW: workflowIcon,
    SUBSCRIPTION: subscriptionIcon,
    CLIENT: crmIcon,
    CUSTOMER: crmIcon,
    REQUEST: requestIcon,
    SERVICE: serviceIcon,
  };

  const getIconForModule = (name) => iconMap[name] || umsIcon;

  const getAuthProviders = async () => {
    setLoading(true);
    setError(null);
    const details = {
      filters: [{ key: "status", eq: "active" }],
      isAllowed: "true",
    };
    try {
      let res = await getUmsAuthProvider(details);

      if (res.data.success) {
        const authProviders = res?.data?.data?.doc || [];
        const modulesFormatted = authProviders.map((authProvider) => ({
          id: authProvider?.publicId,
          name: authProvider?.providerName,
          redirect: authProvider?.redirectUri || "",
          iconSrc: getIconForModule(authProvider?.providerName),
          iconAlt: `${authProvider?.providerName} Icon`,
        }));
        setModules(modulesFormatted);
      } else {
        setError("Failed to fetch modules");
      }
    } catch (error) {
      console.error("Error", error);
      setError(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred while fetching modules",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = (redirectUrl, moduleName) => {
    if (!redirectUrl) {
      notifyWarn("Redirection URL is missing");
      return;
    }
    if (moduleName === "UMS" && canAccessEmployeeKey) {
      const employeeRedirectUrl = redirectUrl && redirectUrl.endsWith("/");
      if (employeeRedirectUrl) {
        navigate(allRoutes?.employees);
        return;
      }
    }

    window.open(redirectUrl, "_self");
  };

  const handleRetry = () => {
    getAuthProviders();
  };

  // Loading Skeleton Component
  const ModuleSkeleton = () => (
    <div className="relative group flex items-center gap-3 px-4 h-[84px] bg-[#F9FAFB] border border-[#F2F4F7] rounded-xl animate-pulse">
      <div className="w-[40px] h-[40px] bg-[#E5E7EB] rounded-[8px]"></div>
      <div className="h-4 bg-[#E5E7EB] rounded w-20"></div>
    </div>
  );

  // Loading State
  const LoadingState = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
      {[...Array(6)].map((_, index) => (
        <ModuleSkeleton key={index} />
      ))}
    </div>
  );

  // Error State
  const ErrorState = () => (
    <div className="bg-white px-4 py-4 rounded-xl flex flex-col gap-4 mt-5 border border-[#f2f4f7]">
      <div className="flex items-center gap-3">
        <svg
          className="w-5 h-5 text-red-500 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
        <h3 className="font-bold text-[#04080b] text-lg m-0">
          Failed to Load Modules
        </h3>
      </div>
      <p className="text-[#475467] text-sm font-medium">{error}</p>
      <button
        onClick={handleRetry}
        className="self-start px-3 py-2 bg-white text-[#087a85] text-sm font-medium rounded-lg border border-[#f2f4f7] hover:bg-[#f9fafb] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#087a85] focus:ring-offset-2"
      >
        Try Again
      </button>
    </div>
  );

  // No Modules Found State
  const NoModulesState = () => (
    <div className="bg-[#FEF3F2] px-6 py-6 rounded-lg flex gap-4 mt-5 border-[1px] border-solid border-[#B42318]">
      <h1 className="font-bold text-[#B42318] w-full text-center text-base">
        No Modules Found
      </h1>
    </div>
  );

  // Content renderer
  const renderContent = () => {
    if (loading) {
      return <LoadingState />;
    }

    if (error) {
      return <ErrorState />;
    }

    if (!modules || modules.length === 0) {
      return <NoModulesState />;
    }

    const checkModuleAccess = (moduleName) => {
      // Check if user has any module permissions (excluding UMS)
      const hasAnyModulePermission = modules.some(
        (m) => m.name !== "UMS" && grantModulePermissions(m.name),
      );

      if (moduleName === "UMS") {
        if (hasAnyModulePermission) {
          // Has other permissions - UMS must be explicitly granted
          return grantModulePermissions("UMS");
        } else {
          // No other permissions - check canAccessEmployee
          return canAccessEmployeeCheck !== "false";
        }
      }

      return grantModulePermissions(moduleName);
    };

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
        {modules.map((module, index) => {
          const hasAccess = checkModuleAccess(module.name);

          if (!hasAccess) return;

          if (!hasAccess) return;

          return (
            <div
              key={index}
              onClick={() =>
                hasAccess && handleModuleClick(module.redirect, module.name)
              }
              className={`relative group flex items-center gap-3 px-4 h-[84px] bg-[#F9FAFB] border border-[#F2F4F7] rounded-xl transition-all duration-200 ${
                hasAccess
                  ? "cursor-pointer hover:shadow-md"
                  : "cursor-not-allowed"
              }`}
            >
              {/* Soft blur layer */}
              {!hasAccess && (
                <div className="absolute inset-0 z-10 bg-white/50 backdrop-blur-[1px] rounded-xl pointer-events-none" />
              )}

              {/* No Access Badge */}
              {!hasAccess && (
                <div className="absolute top-2 right-2 z-20">
                  <span className="text-[10px] font-medium text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] px-2 py-0.5 rounded-full shadow-sm">
                    No Access
                  </span>
                </div>
              )}

              {/* Icon and Module Name */}
              <div className="w-[40px] h-[40px] bg-[#f2f4f7] rounded-[8px] flex justify-center items-center z-0">
                <img
                  src={module.iconSrc}
                  alt={module.iconAlt}
                  className="w-[24px]"
                />
              </div>
              <span
                className={`text-sm font-medium z-0 ${
                  hasAccess ? "text-[#04080B]" : "text-gray-500"
                }`}
              >
                {module.name}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <img className="bg-design" src={bgDesign} alt="background design" />

      <div className="login-main main-added row g-0">
        <div className="content-container-forget">
          <div className="get-otp-container flex flex-column justify-center items-center">
            <div className="awn-logo-container flex justify-center items-center">
              <img className="awn-logo" src={awnLogo} alt="wosol logo" />
            </div>
            <div className="get-otp-content">{renderContent()}</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckForPermittedModules;
